import { useForm, getFormProps } from '@conform-to/react'
import { invariantResponse } from '@epic-web/invariant'
import { json, type LoaderFunctionArgs, type MetaFunction } from '@remix-run/node'
import { useFetcher, useLoaderData } from '@remix-run/react'
import { type PropsWithChildren } from 'react'
import { Link } from '#app/components/link'
import { TeamLogo } from '#app/components/team-logo'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '#app/components/ui/dropdown-menu'
import { Icon } from '#app/components/ui/icon'
import { TabSection, TabList, TabListItem, TabContent } from '#app/components/ui/tab-section'
import { Table, TableHead, TableHeadCell, TableBody, TableBodyCell } from '#app/components/ui/table'
import {
  MAX_ACTIVE_ROSTER_SIZE,
  MAX_RESERVE_ROSTER_SIZE,
  Position,
  RosterStatus,
} from '#app/constants'
import {
  getActiveContract,
  getFormattedEndDate,
  getTenDayContractsForCurrentSeason,
} from '#app/services/contracts.server'
import {
  getPlayerAge,
  isOnActiveRoster,
  isOnReserveRoster,
  isOnInjuredRoster,
  isOnDrafteeRoster,
} from '#app/services/players.server'
import { prisma } from '#app/utils/db.server'
import {
  formatContractLength,
  formatPlayerHandedness,
  formatPlayerNameWithCaptaincy,
  formatPlayerPosition,
} from '#app/utils/formatters'
import { generateMetaTags } from '#app/utils/misc'
import { useOptionalUser, userHasRole } from '#app/utils/user'

const tabPillClasses =
  'text-body-sm ml-2 rounded-full border border-gray-7 bg-gray-3 px-2 py-1 group-data-[state=active]:border-accent-8 group-data-[state=active]:bg-accent-3'

export async function loader({ params }: LoaderFunctionArgs) {
  const team = await prisma.team.findFirst({
    select: {
      id: true,
      name: true,
      generalManager: true,
      headCoach: true,
      assistantCoaches: { select: { name: true }, orderBy: { name: 'asc' } },
      teamLogos: { select: { id: true, altText: true }, where: { current: true } },
    },
    where: { id: parseInt(params.teamId!) },
  })

  invariantResponse(team, 'Team not found', { status: 404 })

  const roster = await prisma.teamPlayer.findMany({
    include: { player: { include: { contracts: true } } },
    where: { teamId: team.id, NOT: { rosterStatus: RosterStatus.FORMER } },
  })

  const parsedRoster = roster.map((rosterPlayer) => {
    const activeContract = getActiveContract(rosterPlayer.player.contracts)

    return {
      id: rosterPlayer.playerId,
      acquiredThrough: rosterPlayer.acquiredThrough,
      activeContractEndDate: activeContract == null ? 'N/A' : getFormattedEndDate(activeContract),
      activeContractLength: activeContract == null ? 'N/A' : formatContractLength(activeContract),
      age: getPlayerAge(rosterPlayer.player),
      shoots: rosterPlayer.player.shoots,
      name: rosterPlayer.player.name,
      captain: rosterPlayer.player.captain,
      number: rosterPlayer.player.number,
      position: rosterPlayer.player.position,
      rosterId: rosterPlayer.id,
      rosterStatus: rosterPlayer.rosterStatus,
      numTenDayContracts: getTenDayContractsForCurrentSeason(rosterPlayer.player.contracts).length,
    }
  })

  return json({
    team,
    fullRoster: parsedRoster,
    rosterLengths: {
      activeRoster: parsedRoster.filter((player) => isOnActiveRoster(player)).length,
      reserveRoster: parsedRoster.filter((player) => isOnReserveRoster(player)).length,
      injuredRoster: parsedRoster.filter((player) => isOnInjuredRoster(player)).length,
      drafteeRoster: parsedRoster.filter((player) => isOnDrafteeRoster(player)).length,
    },
  })
}

export default function TeamPage() {
  const { team, rosterLengths } = useLoaderData<typeof loader>()
  const user = useOptionalUser()
  const isAdminUser = userHasRole(user, 'admin')

  const renderHeading = (className: string) => (
    <div className={className}>
      <h1 className="text-h1 text-gray-12">{team.name}</h1>
      {isAdminUser && (
        <Link to={`/teams/${team.id}/edit`} variant="buttonSecondary">
          <Icon name="pencil-1" />
        </Link>
      )}
    </div>
  )

  return (
    <main className="container mb-32 max-w-6xl">
      <section className="flex flex-col justify-between gap-2 sm:flex-row sm:gap-8">
        {renderHeading('mb-1 flex items-center sm:hidden justify-between')}
        <div className="sm:order-last">
          <TeamLogo logo={team.teamLogos[0]} className="h-28 min-w-28 sm:h-56 sm:min-w-56" />
        </div>
        <div>
          {renderHeading('mb-3 hidden gap-4 sm:flex items-center justify-between')}
          <div className="text-body-lg mb-3 leading-8">
            <p>
              <span className="font-medium text-gray-12">General Manager: </span>
              {team.generalManager}
            </p>
            <p>
              <span className="font-medium text-gray-12">Head Coach: </span>
              {team.headCoach}
            </p>
            {team.assistantCoaches.length > 0 && (
              <p>
                <span className="font-medium text-gray-12">Asst. Coaches: </span>
                {team.assistantCoaches.map(({ name }) => name).join(', ')}
              </p>
            )}
          </div>
        </div>
      </section>

      <section className="mt-6">
        <h2 className="text-h2 text-gray-12">Roster</h2>
        <TabSection defaultValue="active" orientation="horizontal">
          <TabList>
            <TabListItem value="active">
              Active
              <span className={tabPillClasses}>
                {rosterLengths.activeRoster} / {MAX_ACTIVE_ROSTER_SIZE}
              </span>
            </TabListItem>
            <TabListItem value="reserve">
              Reserve
              <span className={tabPillClasses}>
                {rosterLengths.reserveRoster} / {MAX_RESERVE_ROSTER_SIZE}
              </span>
            </TabListItem>
            <TabListItem value="injured">
              Injured
              <span className={tabPillClasses}>{rosterLengths.injuredRoster}</span>
            </TabListItem>
            <TabListItem value="draftees">
              Drafted
              <span className={tabPillClasses}>{rosterLengths.drafteeRoster}</span>
            </TabListItem>
          </TabList>

          <TabContent value="active">
            <RosterTable
              statuses={[RosterStatus.ACTIVE_ROSTER]}
              position={Position.FORWARD}
              label="Forwards"
            />
            <RosterTable
              statuses={[RosterStatus.ACTIVE_ROSTER]}
              position={Position.DEFENSE}
              label="Defenders"
            />
            <RosterTable
              statuses={[RosterStatus.ACTIVE_ROSTER]}
              position={Position.GOALIE}
              label="Goalies"
            />
          </TabContent>
          <TabContent value="reserve">
            <RosterTable
              statuses={[RosterStatus.RESERVE_ROSTER]}
              noPlayersLabel="No players currently on the reserve roster"
            />
          </TabContent>
          <TabContent value="injured">
            <RosterTable
              statuses={[RosterStatus.INJURED_RESERVE, RosterStatus.LONG_TERM_INJURED_RESERVE]}
              noPlayersLabel="No players currently on injured reserve"
            />
          </TabContent>
          <TabContent value="draftees">
            <RosterTable
              statuses={[RosterStatus.DRAFT_PICK]}
              noPlayersLabel="No unsigned draft picks"
            />
          </TabContent>
        </TabSection>
      </section>
    </main>
  )
}

function RosterTable({
  statuses,
  noPlayersLabel = 'No players found',
  position,
  label,
}: {
  statuses: RosterStatus[]
  noPlayersLabel?: string
  position?: Position
  label?: string
}) {
  const user = useOptionalUser()
  const isAdminUser = userHasRole(user, 'admin')

  const showNumberColumn = !statuses.includes(RosterStatus.DRAFT_PICK)
  const showPositionColumn = !statuses.includes(RosterStatus.ACTIVE_ROSTER)
  const showTenDayColumn = statuses.includes(RosterStatus.RESERVE_ROSTER)
  const showContractColumns = [
    RosterStatus.ACTIVE_ROSTER,
    RosterStatus.INJURED_RESERVE,
    RosterStatus.LONG_TERM_INJURED_RESERVE,
  ].some((status) => statuses.includes(status))

  const { fullRoster } = useLoaderData<typeof loader>()
  const roster = fullRoster
    .filter((player) => statuses.includes(player.rosterStatus))
    .filter((player) => {
      if (!position) {
        return true
      }

      return player.position === position
    })
    .sort((playerA, playerB) => playerA.number - playerB.number)

  const handednessLabel = statuses.includes(RosterStatus.ACTIVE_ROSTER)
    ? position === Position.GOALIE
      ? 'Catches'
      : 'Shoots'
    : 'Handedness'

  if (roster.length < 1) {
    return (
      <div className="my-6">
        <p className="text-h6 text-gray-12">{noPlayersLabel}</p>
      </div>
    )
  }

  return (
    <div className="my-6">
      {label && (
        <h3 className="text-h6 mb-3 text-gray-12">
          {label} - {roster.length}
        </h3>
      )}
      <Table>
        <TableHead>
          {showNumberColumn && (
            <TableHeadCell>
              # <span className="sr-only">Number</span>
            </TableHeadCell>
          )}
          <TableHeadCell className="text-left">Name</TableHeadCell>
          <TableHeadCell>Age</TableHeadCell>
          {showPositionColumn && <TableHeadCell>Position</TableHeadCell>}
          <TableHeadCell>{handednessLabel}</TableHeadCell>
          {showContractColumns && (
            <>
              <TableHeadCell>Acquired</TableHeadCell>
              <TableHeadCell>Length</TableHeadCell>
              <TableHeadCell>Expires</TableHeadCell>
            </>
          )}
          {showTenDayColumn && <TableHeadCell>Ten Day Contracts</TableHeadCell>}
          {isAdminUser && (
            <TableHeadCell>
              <span className="sr-only">Actions</span>
            </TableHeadCell>
          )}
        </TableHead>
        <TableBody>
          {roster.map((player) => (
            <tr key={player.id} className="text-body-sm text-center even:bg-gray-2">
              {showNumberColumn && (
                <TableBodyCell>{player.number === 0 ? '' : player.number}</TableBodyCell>
              )}
              <TableBodyCell className="text-left font-medium text-gray-12">
                <Link to={`/players/${player.id}`}>{formatPlayerNameWithCaptaincy(player)}</Link>
              </TableBodyCell>
              <TableBodyCell>{player.age}</TableBodyCell>
              {showPositionColumn && <TableBodyCell>{formatPlayerPosition(player)}</TableBodyCell>}
              <TableBodyCell>{formatPlayerHandedness(player)}</TableBodyCell>
              {showContractColumns && (
                <>
                  <TableBodyCell>{player.acquiredThrough}</TableBodyCell>
                  <TableBodyCell>{player.activeContractLength}</TableBodyCell>
                  <TableBodyCell>{player.activeContractEndDate}</TableBodyCell>
                </>
              )}
              {showTenDayColumn && <TableBodyCell>{player.numTenDayContracts} / 2</TableBodyCell>}
              {isAdminUser && (
                <TableBodyCell>
                  <PlayerActions player={player} />
                </TableBodyCell>
              )}
            </tr>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function PlayerActions({
  player,
}: {
  player: { id: number; rosterStatus: string; rosterId: number }
}) {
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button>
          <Icon name="dots-horizontal">
            <span className="sr-only">Open actions Menu</span>
          </Icon>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {player.rosterStatus !== RosterStatus.ACTIVE_ROSTER && (
          <DropdownMenuItem asChild>
            <PlayerActionForm player={player} status={RosterStatus.ACTIVE_ROSTER}>
              Move to Active Roster
            </PlayerActionForm>
          </DropdownMenuItem>
        )}
        {player.rosterStatus !== RosterStatus.RESERVE_ROSTER && (
          <DropdownMenuItem asChild>
            <PlayerActionForm player={player} status={RosterStatus.RESERVE_ROSTER}>
              Move to Reserve Roster
            </PlayerActionForm>
          </DropdownMenuItem>
        )}
        {player.rosterStatus !== RosterStatus.INJURED_RESERVE && (
          <DropdownMenuItem asChild>
            <PlayerActionForm player={player} status={RosterStatus.INJURED_RESERVE}>
              Move to Injured Reserve
            </PlayerActionForm>
          </DropdownMenuItem>
        )}
        {player.rosterStatus !== RosterStatus.LONG_TERM_INJURED_RESERVE && (
          <DropdownMenuItem asChild>
            <PlayerActionForm player={player} status={RosterStatus.LONG_TERM_INJURED_RESERVE}>
              Move to LTIR
            </PlayerActionForm>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem asChild>
          <PlayerActionForm player={player} status={RosterStatus.FORMER}>
            Remove from team
          </PlayerActionForm>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function PlayerActionForm({
  player,
  status,
  className,
  children,
}: PropsWithChildren<{
  player: { id: number; rosterStatus: string; rosterId: number }
  status: string
  className?: string
}>) {
  const fetcher = useFetcher<{ result: any }>()

  const [form] = useForm({
    id: `change-roster-status-${status}-${player.id}`,
    lastResult: fetcher.data?.result,
  })

  return (
    <fetcher.Form
      {...getFormProps(form)}
      method="POST"
      action={`/players/${player.id}/roster-status`}
      className="w-full"
    >
      <input name="rosterId" type="hidden" value={player.rosterId} />
      <input name="status" type="hidden" value={status} />
      <button type="submit" className={className}>
        {children}
      </button>
    </fetcher.Form>
  )
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const teamName = data?.team.name
  const teamLogo = data?.team?.teamLogos?.[0]?.id

  const title = `${teamName} | CapBuddy`
  const description = `Team profile for ${teamName} on CapBuddy`
  const logoUrl = teamLogo != null ? `/resources/team-logo/${teamLogo}` : undefined

  return [...generateMetaTags(title, description, logoUrl), { title: `${teamName} | CapBuddy` }]
}
